/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Add, Cancel, Close, Save } from "@mui/icons-material";
import {
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import PageContainer from "@jumbo/components/PageContainer";
import LoadingButton from "@jumbo/components/JumboLoadingButton";
import Button from "@jumbo/components/JumboLoadingButton";
import { OFFICE_PRE_BOOK_ORDERS } from "app/routes/constant";
import { getProductList } from "app/redux/slice/productSlice";
import { getVariantList } from "app/redux/slice/variantSlice";
import {
  createPrebookOrder,
  getByIdPrebookOrder,
  updatePrebookOrder,
} from "app/redux/slice/prebookOrderSlice";
import { getSupplierList } from "app/redux/slice/supplierSlice";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import Loader from "@jumbo/components/Loader";
import { istDateInput, istToday } from "app/helpers/istDate";
import { SET_PIECES, isSetProduct } from "app/helpers/setProduct";

const useStyles = makeStyles({
  headingContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  mainInfoContainer: {
    display: "flex",
    minHeight: "170px",
    justifyContent: "center",
    flexDirection: "column",
  },
  headingTitleContainer: {
    display: "flex",
    flexGrow: "wrap",
  },
  headingTitle: {
    minWidth: "80px",
  },
  headingContent: {
    fontWeight: "500",
  },
  qrContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  table: {
    minWidth: 650,
    "& .MuiTableCell-root": {
      borderLeft: "1px solid",
      borderBottom: "1px solid",
    },
  },
  particular: {
    minWidth: "350px",
  },
  variants: {
    minWidth: "300px",
  },
  amount: {
    minWidth: "100px",
    verticalAlign: "top",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "end",
    margin: "10px 0px",
  },
  margin: {
    "& > :first-child": {
      marginTop: "10px",
    },
    marginBottom: "10px",
  },
  alignTop: {
    verticalAlign: "top",
  },
  loadingButtonContainer: {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
  },
});

const validationSchema = yup.object({
  supplierId: yup.object().required("Please select vendor").nullable(),
  date: yup.string().required("Please enter date."),
  products: yup.array().of(
    yup.object().shape({
      productId: yup.object().required("Please select product").nullable(),
      variantId: yup.object().required("Please select variant").nullable(),
      quantity: yup
        .number()
        .typeError("Quantity must be a number")
        .required("Please enter quantity")
        .min(1, "Quantity must be at least 1"),
    }),
  ),
});

const AddAndUpdateOrderPrebook = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const mobileView = useMediaQuery("(max-width:575px)");

  const supplierData = useSelector((state) => state?.supplier);
  const productData = useSelector((state) => state?.product);
  const variantData = useSelector((state) => state?.variant);

  const [isUpdate, setIsUpdate] = useState(false);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      supplierId: null,
      date: istToday(),
      products: [
        {
          _id: 1,
          productId: null,
          variantId: null,
          quantity: "",
        },
      ],
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: async (values, { resetForm, setFieldError, setSubmitting }) => {
      const productArr = [];
      values.products?.forEach((val) => {
        productArr.push({
          productId: val?.productId?._id,
          variantId: val?.variantId?._id,
          quantity: val.quantity,
        });
      });
      setSubmitting(true);
      const newPrebookOrder = {
        supplierId: values?.supplierId?._id,
        date: values.date,
        products: productArr,
      };
      const response = await dispatch(
        id
          ? updatePrebookOrder({ id, data: newPrebookOrder })
          : createPrebookOrder(newPrebookOrder),
      ).unwrap();
      if (response.status === 200) {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "success",
        });
        navigate(OFFICE_PRE_BOOK_ORDERS);
        resetForm();
      } else {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "error",
        });
      }
      setSubmitting(false);
    },
  });

  const { values, setFieldValue, errors, isSubmitting } = formik;
  const productsLength = values?.products?.length;

  const handleChange = (index, field, value) => {
    let updatedFields = values.products;
    updatedFields[index][field] = value;
    // A set product always goes out as a set of 6 pieces, so its Qty starts at
    // 6 (still editable, e.g. 12 for two sets) - the PO raised from this
    // pre-book then opens with 6 weight rows per set.
    if (field === "productId" && isSetProduct(value)) {
      updatedFields[index].quantity = SET_PIECES;
    }
    setFieldValue("products", updatedFields);
  };

  const handleAddMore = () => {
    formik.setValues({
      ...formik.values,
      products: [
        ...formik.values.products,
        {
          productId: null,
          variantId: null,
          quantity: "",
        },
      ],
    });
  };

  const handleAddMoreColor = () => {
    const lastProduct =
      values?.products[values?.products?.length - 1]?.productId || null;
    formik.setValues({
      ...formik.values,
      products: [
        ...formik.values.products,
        {
          productId: lastProduct,
          variantId: null,
          // Same product as the row above, so a set product starts at 6 here too.
          quantity: isSetProduct(lastProduct) ? SET_PIECES : "",
        },
      ],
    });
  };

  const handleDeleteField = (index) => {
    let updatedFields = values.products;
    updatedFields.splice(index, 1);
    setFieldValue("products", updatedFields);
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      dispatch(getByIdPrebookOrder(id))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const prebookDetail = response?.data?.data?.prebookDetail;
            let productArr = [];
            setFieldValue(
              "date",
              istDateInput(prebookDetail?.date),
            );
            setFieldValue("supplierId", prebookDetail?.supplierId);
            prebookDetail?.products?.forEach((val) => {
              productArr.push({
                productId: val?.productId,
                variantId: val?.variantId,
                quantity: val?.quantity,
              });
            });
            setFieldValue("products", productArr);
            setIsUpdate(prebookDetail.isPOCreated === true ? false : true);
            setLoading(false);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
            setLoading(false);
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id, setFieldValue]);

  useEffect(() => {
    dispatch(getProductList({ field: "name", sort: 1 }));
    dispatch(getVariantList({ field: "name", sort: 1 }));
    dispatch(getSupplierList({ sort: 1, field: "name", type: "Office" }));
  }, []);

  const { handleSubmit } = formik;
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <PageContainer
          heading={id ? "Update Pre-Book Order" : "Add Pre-Book Order"}
        >
          <Card>
            <CardContent>
              <FormikProvider value={formik}>
                <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
                  <Grid container border={1}>
                    <Grid item sm={3} xs={12} borderRight={{ xs: 0, sm: 1 }}>
                      <Grid>
                        <Grid px={1} pt={1} mb={1}>
                          <JumboAutoComplete
                            options={supplierData.list ?? []}
                            label="Vendor*"
                            size="small"
                            getOptionLabel={(option) => option?.name}
                            name={"supplierId"}
                            value={values?.supplierId}
                            onChange={(evt, value) => {
                              setFieldValue("supplierId", value);
                            }}
                            helperText={formik?.errors?.supplierId}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item sm={9} xs={12}>
                      <Grid container>
                        <Grid
                          item
                          sm={4}
                          xs={12}
                          borderRight={{ xs: 0, sm: 1 }}
                          p={1}
                        >
                          <TextField
                            label={"Date"}
                            name="date"
                            fullWidth
                            size="small"
                            type="date"
                            onChange={(evt) =>
                              setFieldValue("date", evt.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                            value={values?.date}
                            error={errors?.date}
                            helperText={errors?.date}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    borderRight={1}
                    borderBottom={1}
                    sx={{
                      mt: { xs: 3, md: 0 },
                    }}
                    className="mobile-view-table"
                  >
                    <TableContainer component={Paper}>
                      <Table
                        sx={{ minWidth: 650 }}
                        size="small"
                        aria-label="a dense table"
                        className={classes?.table}
                      >
                        <TableHead>
                          <TableRow>
                            {!mobileView ? <TableCell>Sr. No.</TableCell> : ""}
                            <TableCell className={classes?.particular}>
                              Product
                            </TableCell>
                            <TableCell className={classes?.variants}>
                              Variant
                            </TableCell>
                            <TableCell>Qty</TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {productsLength &&
                            values?.products?.map((item, index) => {
                              return (
                                <TableRow>
                                  {!mobileView ? (
                                    <TableCell sx={{ width: "10px" }}>
                                      {index + 1}
                                    </TableCell>
                                  ) : (
                                    ""
                                  )}
                                  <TableCell className={classes?.particular}>
                                    <JumboAutoComplete
                                      options={productData.list ?? []}
                                      label="Product*"
                                      size="small"
                                      getOptionLabel={(option) => option?.name}
                                      name={`products[${index}].productId`}
                                      value={item?.productId}
                                      onChange={(evt, value) => {
                                        handleChange(index, "productId", value);
                                        handleChange(index, "variantId", null);
                                      }}
                                      helperText={
                                        formik?.errors?.products?.length &&
                                        formik?.errors?.products[index]
                                          ?.productId
                                      }
                                    />
                                  </TableCell>
                                  <TableCell className={classes?.particular}>
                                    <JumboAutoComplete
                                      options={
                                        item?.productId
                                          ? variantData?.list.filter(
                                              (list) =>
                                                list?.product?._id ===
                                                item?.productId?._id,
                                            ) || []
                                          : []
                                      }
                                      label="Variant*"
                                      size="small"
                                      getOptionLabel={(option) => option?.name}
                                      name={`products[${index}].variantId`}
                                      value={item?.variantId}
                                      onChange={(evt, value) =>
                                        handleChange(index, "variantId", value)
                                      }
                                      helperText={
                                        formik?.errors?.products?.length &&
                                        formik?.errors?.products[index]
                                          ?.variantId
                                      }
                                    />
                                  </TableCell>

                                  <TableCell className={classes?.variants}>
                                    <TextField
                                      placeholder="Qty"
                                      name={`products[${index}].quantity`}
                                      fullWidth
                                      value={item?.quantity}
                                      size="small"
                                      type="text"
                                      onChange={(evt) =>
                                        handleChange(
                                          index,
                                          "quantity",
                                          evt.target.value,
                                        )
                                      }
                                      error={
                                        !!(
                                          errors?.products?.length &&
                                          errors?.products[index]?.quantity
                                        )
                                      }
                                      helperText={
                                        (errors?.products?.length &&
                                          errors?.products[index]?.quantity) ||
                                        (isSetProduct(item?.productId)
                                          ? `1 set = ${SET_PIECES} pcs`
                                          : "")
                                      }
                                    />
                                  </TableCell>
                                  <TableCell sx={{ width: "10px" }}>
                                    {values?.products?.length > 1 && (
                                      <IconButton
                                        aria-label="delete"
                                        color="secondary"
                                        onClick={() => handleDeleteField(index)}
                                      >
                                        <Cancel />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}

                          <TableRow>
                            <TableCell
                              colSpan={3}
                              style={{ borderBottom: 0 }}
                            ></TableCell>
                            <TableCell colSpan={2}>
                              <Grid className={classes?.buttonContainer}>
                                <Button
                                  style={{ minWidth: "160px" }}
                                  type="button"
                                  buttonLabel={"Add Color"}
                                  startIcon={<Add />}
                                  variant="outlined"
                                  onClick={handleAddMoreColor}
                                  fullWidth={mobileView}
                                />
                              </Grid>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={3}></TableCell>
                            <TableCell colSpan={2}>
                              <Grid className={classes?.buttonContainer}>
                                <Button
                                  style={{ minWidth: "160px" }}
                                  type="button"
                                  buttonLabel={"Add Product"}
                                  startIcon={<Add />}
                                  variant="outlined"
                                  onClick={handleAddMore}
                                  fullWidth={mobileView}
                                />
                              </Grid>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <br></br>
                  <Grid container spacing={3}>
                    <Grid
                      item
                      sm={12}
                      xs={12}
                      className={classes?.loadingButtonContainer}
                    >
                      <LoadingButton
                        disabled={id && !isUpdate}
                        type="submit"
                        buttonLabel={id ? "Update" : "Save"}
                        sx={{ marginRight: "10px" }}
                        startIcon={<Save />}
                        variant="outlined"
                        fullWidth={mobileView}
                        // Disables itself while the request is in flight, so a
                        // double-click can't submit the same order twice.
                        loading={isSubmitting}
                      />
                      <LoadingButton
                        color={"secondary"}
                        buttonLabel={"Close"}
                        variant="outlined"
                        startIcon={<Close />}
                        onClick={() => navigate(OFFICE_PRE_BOOK_ORDERS)}
                        fullWidth={mobileView}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </FormikProvider>
            </CardContent>
          </Card>
        </PageContainer>
      )}
    </>
  );
};

export default AddAndUpdateOrderPrebook;
